"use client";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Card, CardContent } from "~/components/v3/shadcn/Card";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { EventPreview } from "~/components/v3/events-components/EventPreview";
import Image from "next/image";
import { ApplyToSponsorFormData } from "./types";

type Props = {
    events: EventBySlugOutput[];
};

export const SponsorStep3 = ({ events }: Props) => {
    const { watch } = useFormContext<ApplyToSponsorFormData>();
    const {
        selectedPackageTier,
        name,
        email,
        description,
        image,
        socialLinks,
    } = watch();

    return (
        <div className="bg-black p-4 text-white sm:p-8">
            <h2 className="mb-6 text-2xl font-bold">Summary</h2>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card>
                    <CardContent className="p-6">
                        <h3 className="mb-4 text-xl font-bold">Ad Preview</h3>
                        <div className="space-y-4">
                            {image && (
                                <div className="mb-4">
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/events/${events[0]?.slug}/sponsors/${image}`}
                                        alt="Sponsor Image"
                                        width={300}
                                        height={200}
                                        objectFit="cover"
                                        className="rounded-lg"
                                    />
                                </div>
                            )}
                            <p>
                                <strong>Ad Name:</strong> {name ?? "N/A"}
                            </p>
                            <p>
                                <strong>Email:</strong> {email ?? "N/A"}
                            </p>
                            <p>
                                <strong>Description:</strong>
                            </p>
                            <p className="text-sm">{description ?? "N/A"}</p>
                            <div>
                                <p>
                                    <strong>Social Links:</strong>
                                </p>
                                <ul className="list-disc pl-5 text-sm">
                                    {socialLinks?.x && (
                                        <li>Twitter: @{socialLinks.x}</li>
                                    )}
                                    {socialLinks?.li && (
                                        <li>LinkedIn: {socialLinks.li}</li>
                                    )}
                                    {socialLinks?.ig && (
                                        <li>Instagram: {socialLinks.ig}</li>
                                    )}
                                    {socialLinks?.fb && (
                                        <li>Facebook: {socialLinks.fb}</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <h3 className="mb-4 text-xl font-bold">
                            Selected Events ({events.length})
                        </h3>
                        <div className="max-h-[300px] space-y-4 overflow-y-auto">
                            {events.map((event) => (
                                <div
                                    key={event.id}
                                    className="border-b border-v3-N-400 pb-4 last:border-b-0"
                                >
                                    <EventPreview event={event} />
                                </div>
                            ))}
                        </div>
                        <h3 className="mb-4 mt-6 text-xl font-bold">
                            Selected Plan
                        </h3>
                        <h4 className="mb-2 text-lg font-semibold">
                            {selectedPackageTier?.name} 🍷
                        </h4>
                        <p className="mb-6 text-sm">
                            {selectedPackageTier?.description}
                        </p>
                        <h3 className="mb-4 text-xl font-bold">Total</h3>
                        <p className="mb-2 text-sm">
                            Sponsorship Fee ($
                            {selectedPackageTier?.priceDollars} ×{" "}
                            {events.length} events)
                        </p>
                        <p className="text-3xl font-bold">
                            ${selectedPackageTier?.priceDollars}.
                            {selectedPackageTier?.priceCents}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
